// apps/widget/src/screens/mobile-capture.screen.tsx
//
// The phone half of the handoff, reached by scanning a QR code.
//
// It captures, uploads, and then says "check your computer". The report is NOT
// shown here — the visitor came to the desktop for it, and duplicating it on the
// phone splits their attention at the exact moment the CTA appears.
//
// The camera, live guidance and countdown are the desktop's own <Viewfinder>,
// so the phone guides the visitor exactly as the computer does.

import { useEffect, useRef, useState } from 'react';
import { detect, preloadFaceMesh } from '../lib/facemesh';
import { downscale, uploadPhoto } from '../lib/upload';
import { api, type WidgetConfig } from '../lib/api';
import { t } from '../lib/i18n';
import { PrepareContent } from '../components/capture-tips';
import { Viewfinder, type Shot } from '../components/viewfinder';
import { LanguageSwitch } from '../components/widget-header';

interface Props {
  sessionId: string;
  token: string;
  config: WidgetConfig;
}

export function MobileCaptureScreen({ sessionId, token, config }: Props) {
  // The phone shows the photo tips first: load the face model while they read.
  useEffect(() => preloadFaceMesh(), []);
  const retainPhoto = useRef(false);
  const [joined, setJoined] = useState(false);
  const [done, setDone] = useState(false);
  // The photo tips first; the camera opens once they've read them.
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Burn the single-use token first. A phone that cannot join must not be shown
  // a camera it will only fail to upload from.
  useEffect(() => {
    void (async () => {
      try {
        const joinedSession = await joinOnce(sessionId, token);
        retainPhoto.current = joinedSession.retainPhoto === true;
        setJoined(true);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'This link is no longer valid.');
      }
    })();
  }, [sessionId, token]);

  const upload = async ({ canvas, geometry, setProgress, stopCamera }: Shot): Promise<void> => {
    try {
      // Re-detected on the frame actually taken, as on the desktop, and only
      // when the visitor agreed (on the desktop) to keep their photo.
      const pinned = retainPhoto.current ? detect(canvas, performance.now()) : null;
      const blob = await downscale(canvas);
      const { imageKey } = await uploadPhoto(sessionId, blob, setProgress);
      await api.analyze(sessionId, {
        imageKey,
        ...(geometry ? { faceBox: geometry.box } : {}),
        ...(pinned ? { face: { width: canvas.width, height: canvas.height, landmarks: pinned.landmarks } } : {}),
      });
      stopCamera();
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('error.title'));
    }
  };

  if (error) {
    return (
      <Message
        config={config}
        tone="error"
        title={t('error.title')}
        body={error}
        note={t('mobile.errorRetry')}
      />
    );
  }

  if (done) {
    return (
      <Message
        config={config}
        tone="success"
        title={t('mobile.doneTitle')}
        body={t('mobile.doneBody')}
        note={t('mobile.doneClose')}
      />
    );
  }

  if (!ready) {
    return (
      <div className="gs-mobile-prepare">
        <MobileBrand config={config} />
        <PrepareContent onReady={() => setReady(true)} readyLabel={t('prepare.openCamera')} />
      </div>
    );
  }

  return (
    <div className="gs-mobile-capture">
      <MobileBrand config={config} />
      <Viewfinder
        enabled={joined}
        onCapture={upload}
        // The phone only sends the photo; the scan plays on the computer.
        busyLabel={t('mobile.sending')}
        renderCameraError={(cameraError) => (
          <Message
            config={config}
            tone="error"
            title={t('error.camera')}
            // The desktop's wording offers "use your phone instead", which makes
            // no sense on the phone itself.
            body={cameraError.failure === 'DENIED' ? t('mobile.cameraHelp') : cameraError.message}
            note={t('mobile.errorRetry')}
          />
        )}
      />
    </div>
  );
}

/** The clinic's name or logo, so the visitor knows whose page they are on. */
function MobileBrand({ config }: { config: WidgetConfig }) {
  return (
    <header className="gs-mobile-brand">
      {config.tenant.logoUrl ? (
        <img src={config.tenant.logoUrl} alt={config.tenant.name} />
      ) : (
        <span>{config.tenant.name}</span>
      )}
      <LanguageSwitch available={config.locales} />
    </header>
  );
}

function Message({
  config,
  tone,
  title,
  body,
  note,
}: {
  config: WidgetConfig;
  tone: 'success' | 'error';
  title: string;
  body: string;
  note: string;
}) {
  return (
    <div className="gs-mobile-message" data-tone={tone}>
      <MobileBrand config={config} />
      <div className="gs-mobile-message__body" role={tone === 'error' ? 'alert' : 'status'}>
        <span className="gs-mobile-message__icon" aria-hidden="true">
          {tone === 'success' ? (
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
              <path d="m5 12.5 4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          ) : (
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none">
              <path d="M12 8v5M12 16.5v.01" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            </svg>
          )}
        </span>
        <h1 className="gs-mobile-message__title">{title}</h1>
        <p className="gs-mobile-message__text">{body}</p>
        <p className="gs-mobile-message__note">{note}</p>
      </div>
      {config.showPoweredBy && <p className="gs-powered">{t('common.poweredBy')}</p>}
    </div>
  );
}

/**
 * Joins a handed-off session exactly once per page, and rejoins after a reload.
 *
 * The QR token is single-use. Two joins racing — React's development double
 * effect, or a quick double render — spent it on the first and showed "This QR
 * code has already been used" for the second. And a reload of this tab (the
 * camera permission prompt can cause one) had nothing left to join with. So
 * the in-flight join is shared, and the device code the server returns on the
 * first join is kept in this tab to rejoin with.
 */
const joins = new Map<string, ReturnType<typeof api.join>>();

function joinOnce(sessionId: string, token: string): ReturnType<typeof api.join> {
  const existing = joins.get(sessionId);
  if (existing) return existing;

  const storageKey = `gs.handoff.${sessionId}`;
  let deviceToken: string | null = null;
  try {
    deviceToken = sessionStorage.getItem(storageKey);
  } catch {
    // Storage blocked: the QR token alone still joins the first time.
  }

  const attempt = api
    .join(sessionId, { ...(token ? { token } : {}), ...(deviceToken ? { deviceToken } : {}) })
    .then((result) => {
      if (result.deviceToken) {
        try {
          sessionStorage.setItem(storageKey, result.deviceToken);
        } catch {
          // Not kept: a reload will need a fresh QR code, as before.
        }
      }
      return result;
    });

  joins.set(sessionId, attempt);
  // A failed join may be retried; a successful one is reused.
  attempt.catch(() => joins.delete(sessionId));
  return attempt;
}
